import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram } from "lucide-react";
import { SITE } from "../data/content";

export default function Footer() {
  return (
    <footer className="bg-[var(--ec-navy-deep)] text-[var(--ec-foam)]" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 grid md:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-5">
            <img
              src={SITE.logo}
              alt=""
              className="h-14 w-14 object-contain rounded-xl bg-white p-1"
            />
            <div>
              <div className="font-display text-xl text-white">Elk Creek Marina</div>
              <div className="text-[11px] uppercase tracking-[0.18em] opacity-70">
                Lake Tenkiller, OK
              </div>
            </div>
          </div>
          <p className="text-sm text-white/70 leading-relaxed max-w-xs">
            A quiet, family-friendly resort & marina on the north end of Lake Tenkiller — cabins, boat slips, and a serene lake getaway since 1953.
          </p>
        </div>

        <div>
          <h2 className="text-white font-display text-lg mb-4">Explore</h2>
          <ul className="space-y-2 text-sm text-white/75">
            <li><Link to="/cabins" data-testid="footer-link-cabins" className="hover:text-white transition-colors">Cabin Rentals</Link></li>
            <li><Link to="/marina" data-testid="footer-link-marina" className="hover:text-white transition-colors">Marina & Slips</Link></li>
            <li><Link to="/boating" data-testid="footer-link-boating" className="hover:text-white transition-colors">Boating & Rentals</Link></li>
            <li><Link to="/faq" data-testid="footer-link-faq" className="hover:text-white transition-colors">FAQ</Link></li>
            <li><Link to="/contact" data-testid="footer-link-contact" className="hover:text-white transition-colors">Contact</Link></li>
          </ul>
        </div>

        <div>
          <h2 className="text-white font-display text-lg mb-4">Get in Touch</h2>
          <ul className="space-y-3 text-sm text-white/80">
            <li className="flex items-start gap-2">
              <Phone className="w-4 h-4 mt-0.5 shrink-0 text-[var(--ec-lake-light)]" />
              <a href={`tel:${SITE.phoneRaw}`} className="hover:text-white" data-testid="footer-phone">
                {SITE.phoneDisplay}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <Mail className="w-4 h-4 mt-0.5 shrink-0 text-[var(--ec-lake-light)]" />
              <a href={`mailto:${SITE.email}`} className="hover:text-white break-all" data-testid="footer-email">
                {SITE.email}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-[var(--ec-lake-light)]" />
              <a href={SITE.directions} target="_blank" rel="noopener noreferrer" className="hover:text-white" data-testid="footer-address">
                {SITE.address}<span className="sr-only"> (opens map in new tab)</span>
              </a>
            </li>
          </ul>
        </div>

        <div>
          <h2 className="text-white font-display text-lg mb-4">Plan Your Stay</h2>
          <Link
            to="/contact"
            data-testid="footer-book-cta"
            className="inline-flex items-center justify-center w-full bg-[var(--ec-lake)] hover:bg-[var(--ec-lake-dark)] text-white font-semibold py-3 rounded-full btn-lift mb-4"
          >
            Book Your Stay
          </Link>
          <div className="flex items-center gap-3 mt-4">
            <a
              href={SITE.facebook}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Elk Creek Marina on Facebook (opens in new tab)"
              data-testid="footer-facebook"
              className="w-14 h-14 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-[var(--ec-lake)] transition-colors"
            >
              <Facebook className="w-6 h-6" />
            </a>
            <a
              href={SITE.instagram}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Elk Creek Marina on Instagram (opens in new tab)"
              data-testid="footer-instagram"
              className="w-14 h-14 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-[var(--ec-lake)] transition-colors"
            >
              <Instagram className="w-6 h-6" />
            </a>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-5 flex flex-col md:flex-row gap-2 justify-between items-center text-xs text-white/55">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 justify-center">
            <span data-testid="footer-copyright">
              © {new Date().getFullYear()} Elk Creek Marina. All rights reserved.
            </span>
            {SITE.privacyPolicyApproved && (
              <Link to="/privacy" data-testid="footer-privacy-link" className="underline underline-offset-2 hover:text-white">
                Privacy Policy
              </Link>
            )}
            <Link to="/accessibility" data-testid="footer-accessibility-link" className="underline underline-offset-2 hover:text-white">
              Accessibility
            </Link>
          </div>
          <a
            href="https://builtby-ab.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-built-by"
            className="underline-offset-2 hover:underline hover:text-white"
          >
            Built by AB<span className="sr-only"> (opens in new tab)</span>
          </a>
        </div>
      </div>
    </footer>
  );
}
